import gql from 'graphql-tag';

export const GET_USERS = gql`
  query GET_USERS($where: UserWhereInput, $orderBy: UserOrderByInput, $first: Int, $skip: Int) {
    users(where: $where, orderBy: $orderBy, first: $first, skip: $skip) {
      id
      firstName
      lastName
      email
      emailVerified
      accountLocked
      lockReason
      inDefault
      acceptEmails
      defaultAmount
      photo {
        uri
      }
      advertisements {
        id
        isActive
      }
      packageStations {
        id
        isOnboarded
        isActive
        packages {
          id
        }
      }
      companyRole
      myCompany {
        id
        name
      }
      orders {
        id
      }
    }
    totalUsers(where: $where) {
      total
    }
  }
`;

export type UserQuery = {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    emailVerified: boolean;
    accountLocked: boolean;
    lockReason: string;
    inDefault: boolean;
    defaultAmount: number;
    birthdate: string;
    acceptEmails: boolean;
    stripeId: string;
    address: {
      street: string;
      houseNumber: string;
      postalCode: string;
      city: string;
    };
    companyRole: string;
    myCompany: {
      id: string;
      name: string;
    };
    createdAt: string;
    photo: {
      uri: string;
    };
    orders: {
      id: string;
    }[];
    advertisements: {
      id: string;
      isActive: boolean;
    }[];
    packageStations: {
      id: string;
      isOnboarded: boolean;
      isActive: boolean;
      locationName: string;
      stripeAccountId: string;
      address: {
        street: string;
        houseNumber: string;
        postalCode: string;
        city: string;
      };
      company: {
        id: string;
      };
      packages: {
        id: string;
      }[];
      lastActivityCheckSent: string;
      lastActivityCheckResponse: string;
      activityCheck: string;
      activityCheckSent: string;
      activityCheckToken: string;
      activityCheckAmount: number;
      accessPointDataMigrated: boolean;
      pickupTimes: {
        from: number;
        to: number;
        weekday?: number | null;
        allDays?: boolean | null;
      }[];
      submitTimestamp: string;
      identificationSuccess: boolean;
      identificationTries: number;
      identificationSuccessTimestamp: string;
      openingHours: {
        type: string;
        monday: {
          from: string;
          to: string;
        }[];
        tuesday: {
          from: string;
          to: string;
        }[];
        wednesday: {
          from: string;
          to: string;
        }[];
        thursday: {
          from: string;
          to: string;
        }[];
        friday: {
          from: string;
          to: string;
        }[];
        saturday: {
          from: string;
          to: string;
        }[];
        sunday: {
          from: string;
          to: string;
        }[];
      };
      idFront: {
        uri: string;
      };
      idBack: {
        uri: string;
      };
      idSelfie: {
        uri: string;
      };
      accessPoint?: AccessPoint;
      hubCounter?: HubCounter;
    }[];
    devices: UserDeviceData[];
  };
};

export type UserDeviceData = {
  id: string;
  deviceId: string;
  os: string;
  osVersion: string;
  model: string;
  appVersion: string | null;
  lastLatitude: number | null;
  lastLongitude: number | null;
  lastLocationName: string | null;
  lastUsed: string;
  createdAt: string;
};

export type DropPointQuery = {
  packageStation: {
    id: string;
    taxId: string;
    accountType: string;
    accountHolder: string;
    bankName: string;
    iban: string;
    stationPhotos: {
      uri: string;
    }[];
    identificationMethods: {
      iid: boolean;
      idCheck: boolean;
    };
    pickupTimes: {
      from: number;
      to: number;
    }[];
    packageSize: string;
    packageWeight: string;
    numberOfPackages: number;
    capacity: string;
    activeCoupon: {
      id: string;
      code: string;
      validUntil: string;
      description: string;
    } | null;
    openingHours: AccessPoint['openingHours'] | null;
    accessPoint: Pick<AccessPoint, 'accessPointID' | 'na1adlActive'> | null;
    updatedAt: string;
  } | null;
};

export type AccessPoint = {
  id: string;
  accessPointID: string;
  status: string;
  isActive: boolean;
  submitTimestamp: string;
  identificationSuccess: boolean;
  identificationTries: number;
  identificationSuccessTimestamp: string;
  rejectionReason: string;
  rejectionTimestamp: string;
  na1adlActive: boolean;
  na1adlChangedAt: string;
  openingHours: {
    type: string;
    monday: {
      from: string;
      to: string;
    }[];
    tuesday: {
      from: string;
      to: string;
    }[];
    wednesday: {
      from: string;
      to: string;
    }[];
    thursday: {
      from: string;
      to: string;
    }[];
    friday: {
      from: string;
      to: string;
    }[];
    saturday: {
      from: string;
      to: string;
    }[];
    sunday: {
      from: string;
      to: string;
    }[];
  };
  idFront: {
    uri: string;
  };
  idBack: {
    uri: string;
  };
  idSelfie: {
    uri: string;
  };
  createdAt: string;
  updatedAt: string;
};

export const GET_USER = gql`
  query USER($where: UserWhereUniqueInput!) {
    user(where: $where) {
      id
      firstName
      lastName
      email
      emailVerified
      accountLocked
      lockReason
      inDefault
      defaultAmount
      birthdate
      acceptEmails
      stripeId
      address {
        street
        houseNumber
        postalCode
        city
      }
      companyRole
      myCompany {
        id
        name
      }
      createdAt
      photo {
        uri
      }
      orders {
        id
      }
      advertisements {
        id
        isActive
      }
      packageStations {
        id
        isOnboarded
        isActive
        locationName
        address {
          street
          houseNumber
          postalCode
          city
        }
        company {
          id
        }
        packages {
          id
        }
        lastActivityCheckSent
        lastActivityCheckResponse
        activityCheck
        activityCheckSent
        activityCheckToken
        activityCheckAmount

        stripeAccountId

        accessPointDataMigrated
        pickupTimes {
          from
          to
          weekday
          allDays
        }
        submitTimestamp
        openingHours {
          type
          monday {
            from
            to
          }
          tuesday {
            from
            to
          }
          wednesday {
            from
            to
          }
          thursday {
            from
            to
          }
          friday {
            from
            to
          }
          saturday {
            from
            to
          }
          sunday {
            from
            to
          }
        }
        idFront {
          uri
        }
        idBack {
          uri
        }
        idSelfie {
          uri
        }
        identificationSuccess
        identificationTries
        identificationSuccessTimestamp

        accessPoint {
          id
          accessPointID
          status
          isActive
          na1adlActive
          na1adlChangedAt

          rejectionReason
          rejectionTimestamp

          submitTimestamp
          openingHours {
            type
            monday {
              from
              to
            }
            tuesday {
              from
              to
            }
            wednesday {
              from
              to
            }
            thursday {
              from
              to
            }
            friday {
              from
              to
            }
            saturday {
              from
              to
            }
            sunday {
              from
              to
            }
          }
          idFront {
            uri
          }
          idBack {
            uri
          }
          idSelfie {
            uri
          }
          identificationSuccess
          identificationTries
          identificationSuccessTimestamp

          createdAt
          updatedAt
        }
        hubCounter {
          id
          hubCounterID
          amazonAccessPointId
          name
          status
          isActive
          isStaging
          amazonVerified
          amazonVerifiedAt
          lastFeedId
          lastFeedStatus
          lastDeactivationTimestamp
          deactivatedUntil
          rejectionReason
          rejectionTimestamp
          createdAt
          updatedAt
        }
      }
      devices {
        id
        deviceId
        os
        osVersion
        model
        appVersion
        lastLatitude
        lastLongitude
        lastLocationName
        lastUsed
        createdAt
      }
    }
  }
`;

export const GET_ACCOUNT = gql`
  query ACCOUNT($stationId: String!) {
    retrieveAccount(stationId: $stationId) {
      phone
    }
  }
`;

export const GET_DROPPOINT = gql`
  query GET_DROPPOINT($where: PackageStationWhereUniqueInput!) {
    packageStation(where: $where) {
      id

      taxId
      accountType
      accountHolder
      bankName
      iban

      stationPhotos {
        uri
      }
      identificationMethods {
        iid
        idCheck
      }
      pickupTimes {
        from
        to
      }
      packageSize
      packageWeight
      numberOfPackages
      capacity
      activeCoupon {
        id
        code
        validUntil
        description
      }
      openingHours {
        type
        monday {
          from
          to
        }
        tuesday {
          from
          to
        }
        wednesday {
          from
          to
        }
        thursday {
          from
          to
        }
        friday {
          from
          to
        }
        saturday {
          from
          to
        }
        sunday {
          from
          to
        }
      }
      accessPoint {
        accessPointID
        na1adlActive
      }
      updatedAt
    }
  }
`;

export const HISTORY = gql`
  query HISTORY($where: UserWhereUniqueInput!, $accessPointId: String, $dropPointId: ID) {
    user(where: $where) {
      id
      firstName
      lastName
      birthdate
      address {
        street
        houseNumber
        postalCode
        city
      }
      history(orderBy: createdAt_DESC, first: 500) {
        type
        comment
        createdAt
        change
        changedBy {
          id
          firstName
        }
      }
    }
    accessPoint(id: $accessPointId) {
      id
      history(orderBy: createdAt_DESC, first: 500) {
        type
        comment
        createdAt
        change
        changedBy {
          id
          firstName
        }
      }
    }
    packageStation(where: { id: $dropPointId }) {
      id
      history(orderBy: createdAt_DESC, first: 500) {
        type
        comment
        createdAt
        change
        changedBy {
          id
          firstName
        }
      }
    }
  }
`;

export const EDIT_USER = gql`
  mutation EDIT_USER(
    $firstName: String
    $lastName: String
    $street: String
    $houseNumber: String
    $postalCode: String
    $city: String
    $where: UserWhereUniqueInput!
    $internalUser: String!
    $comment: String
  ) {
    internalUpdateUser(
      firstName: $firstName
      lastName: $lastName
      street: $street
      houseNumber: $houseNumber
      postalCode: $postalCode
      city: $city
      where: $where
      internalUser: $internalUser
      comment: $comment
    ) {
      id
    }
  }
`;

export const SEND_EMAIL = gql`
  mutation {
    sendEmailToDropPoints
  }
`;

export const EXPORT_USERS = gql`
  mutation {
    exportUsersToCsv
  }
`;
export const EXPORT_USERS_BY_WEEK = gql`
  mutation EXPORT_USERS_BY_WEEK($start: Float!, $end: Float!) {
    exportUserStatisticsByWeek(start: $start, end: $end)
  }
`;

export const UPDATE_USER = gql`
  mutation UPDATE_USER($id: ID!, $email: String!, $internalUser: String!, $comment: String) {
    internalChangeEmail(id: $id, email: $email, internalUser: $internalUser, comment: $comment) {
      id
      email
    }
  }
`;

export const DELETE_USER = gql`
  mutation DELETE_USER($userId: String!) {
    deleteUser(userId: $userId)
  }
`;

export const CANCEL_USER = gql`
  mutation CANCEL_USER($userId: String!, $internalUser: String!, $comment: String!) {
    internalCancelUser(userId: $userId, comment: $comment, internalUser: $internalUser)
  }
`;

export const FORCE_CANCEL_USER = gql`
  mutation FORCE_CANCEL_USER($userId: String!, $internalUser: String!, $comment: String!) {
    internalCancelUser(userId: $userId, comment: $comment, internalUser: $internalUser, overrideAccessPointRules: true)
  }
`;

export const LOCK_USER = gql`
  mutation LOCK_USER($userId: String!, $comment: String!, $internalUser: String!, $sendEmail: Boolean) {
    lockUser(userId: $userId, comment: $comment, sendEmail: $sendEmail, internalUser: $internalUser) {
      id
    }
  }
`;

export const SEND_EMAIL_TO_USER = gql`
  mutation SEND_EMAIL_TO_USER($userId: String!, $comment: String!, $internalUser: String!, $templateId: String!, $templateName: String) {
    sendEmailToUser(userId: $userId, comment: $comment, templateId: $templateId, internalUser: $internalUser, templateName: $templateName)
  }
`;

export const UNLOCK_USER = gql`
  mutation UNLOCK_USER($userId: String!, $internalUser: String!, $comment: String!) {
    unlockUser(userId: $userId, comment: $comment, internalUser: $internalUser) {
      id
    }
  }
`;

export const NEW_HISTORY_ENTRY = gql`
  mutation NEW_HISTORY_ENTRY($userId: String!, $internalUser: String!, $comment: String!) {
    internalHistoryEntry(userId: $userId, comment: $comment, internalUser: $internalUser) {
      id
    }
  }
`;

export const GET_ORDERS = gql`
  query GET_ORDERS($where: OrderWhereInput, $orderBy: OrderOrderByInput) {
    orders(where: $where, orderBy: $orderBy) {
      id
      iid
      createdAt
      status
      tip
      packageStation {
        id
        locationName
        company {
          id
          logo
          name
          users {
            id
            email
          }
        }
        user {
          id
          firstName
          lastName
          email
        }
      }
      orderer {
        id
        firstName
        lastName
        email
      }
      pickuper {
        id
        firstName
        lastName
        email
      }
      pickuperNoUser {
        firstName
        lastName
        email
      }
      payment {
        id
        type
      }
      review {
        id
        rating
        note
      }
      stationReview {
        id
        rating
        note
      }
      packages {
        id
        handedOver
        handedOverTimestamp
        timestamp
        holdingFee
        note
        photos {
          uri
        }
      }
    }
    totalOrdersCount(where: $where)
  }
`;

export const ORDER_SUBSCRIPTION = gql`
  subscription ORDER_SUBSCRIPTION($userId: String!) {
    order(userId: $userId) {
      id
      iid
      createdAt
      status
      packages {
        id
        handedOver
        handedOverTimestamp
        timestamp
        note
        photos {
          uri
        }
      }
    }
  }
`;

export const GET_ADVERTISEMENTS = gql`
  query GET_ADVERTISEMENTS($where: AdvertisementWhereInput, $orderBy: AdvertisementOrderByInput, $first: Int, $skip: Int) {
    advertisements(where: $where, orderBy: $orderBy, first: $first, skip: $skip) {
      id
      title
      category
      description
      delivery
      url
      isActive
      deliveryTimes {
        from
        to
        allDays
        weekday
      }
      address {
        street
        houseNumber
        postalCode
        city
        locationId
        geometry {
          type
          coordinates
        }
      }
      photo {
        uri
      }
      user {
        id
        firstName
        lastName
        email
        photo {
          uri
        }
      }
    }
    totalAdvertisements {
      total
    }
  }
`;

export const SEND_ACTIVITY_CHECK = gql`
  mutation SEND_ACTIVITY_CHECK($internalUserId: String!, $where: PackageStationWhereInput!) {
    sendActivityCheck(internalUserId: $internalUserId, where: $where)
  }
`;

export const UPDATE_ACCESS_POINT_STATUS = gql`
  mutation UPDATE_ACCESS_POINT_STATUS(
    $internalUserId: String!
    $dropPointId: String!
    $accessPointID: String
    $status: AccessPointStatus!
    $comment: String
  ) {
    updateStatusByInternalUser(
      internalUserId: $internalUserId
      dropPointId: $dropPointId
      accessPointID: $accessPointID
      status: $status
      comment: $comment
    ) {
      id
    }
  }
`;

export const ACCEPT_IDENTIFICATION = gql`
  mutation ACCEPT_IDENTIFICATION($internalUserId: String!, $dropPointId: String!) {
    acceptAccessPointIdentification(internalUserId: $internalUserId, dropPointId: $dropPointId) {
      id
    }
  }
`;

export const REJECT_IDENTIFICATION = gql`
  mutation REJECT_IDENTIFICATION($internalUserId: String!, $dropPointId: String!) {
    rejectAccessPointIdentification(internalUserId: $internalUserId, dropPointId: $dropPointId) {
      id
    }
  }
`;

export const MANUAL_SYNC = gql`
  mutation MANUAL_SYNC($accessPointID: String!) {
    scanAccessPointOnLaunch(accessPointID: $accessPointID)
  }
`;

export type UPSParcel = {
  accessPointID: string;
  deliveryStatus: string;
  trackingNumber: string;
  consigneeName: string;
  consigneePickupAt: string;
  consigneePickupAckAt: string;
  driverDropOffAt: string;
  driverDropOffAckAt: string;
  parcelAgedAt: string;
  agedParcelScanAt: string;
  agedParcelScanAckAt: string;
  shipperDropOffAt: string;
  shipperDropOffAckAt: string;
  createdAt: string;
  updatedAt: string;
  epraIndicator?: boolean;
  addresseeOnlyIndicator?: boolean;
  adultSignatureIndicator?: boolean;
  userId?: string;
  notInInventorySync?: boolean;
  hiddenInApp?: boolean;
};

export type UPSParcelsQuery = {
  accessPointInventoryByStatus: UPSParcel[];
};

export const GET_UPS_PARCELS = gql`
  query GET_UPS_PARCELS($accessPointID: String!, $status: [String!]!) {
    accessPointInventoryByStatus(accessPointID: $accessPointID, status: $status) {
      accessPointID
      deliveryStatus
      trackingNumber
      consigneeName
      epraIndicator
      addresseeOnlyIndicator
      adultSignatureIndicator
      consigneePickupAt
      consigneePickupAckAt
      driverDropOffAt
      driverDropOffAckAt
      parcelAgedAt
      agedParcelScanAt
      agedParcelScanAckAt
      shipperDropOffAt
      shipperDropOffAckAt
      createdAt
      notInInventorySync
      hiddenInApp
    }
  }
`;

export const SEND_UPS_EMAIL_TO_USERS = gql`
  mutation SEND_UPS_EMAIL_TO_USERS($where: UserWhereInput!) {
    sendUPSEmailToUsers(where: $where)
  }
`;

export const CREATE_STRIPE_ACCOUNT = gql`
  mutation CREATE_STRIPE_ACCOUNT($packageStationId: String!, $comment: String!, $internalUser: String!) {
    internalCreateStripeAccount(packageStationId: $packageStationId, comment: $comment, internalUser: $internalUser)
  }
`;

export const CREATE_STRIPE_CUSTOMER = gql`
  mutation CREATE_STRIPE_CUSTOMER($userId: String!, $comment: String!, $internalUser: String!) {
    internalCreateStripeCustomer(userId: $userId, comment: $comment, internalUser: $internalUser)
  }
`;

export const TOGGLE_ACCESS_POINT_ACTIVE_STATUS = gql`
  mutation TOGGLE_ACCESS_POINT_ACTIVE_STATUS($accessPointID: String!, $active: Boolean!) {
    toggleAccessPointActiveStatus(accessPointID: $accessPointID, active: $active) {
      id
    }
  }
`;

export const SYNC_OPENING_HOURS = gql`
  mutation SYNC_OPENING_HOURS($accessPointID: String!, $userId: String!) {
    internalSyncOpeningHours(accessPointID: $accessPointID, userId: $userId)
  }
`;

export const UPDATE_OPENING_HOURS = gql`
  mutation UPDATE_OPENING_HOURS($dropPointId: String!, $openingHours: OpeningHoursCreateInput!, $userId: String!) {
    internalUpdateOpeningHours(dropPointId: $dropPointId, openingHours: $openingHours, userId: $userId) {
      id
    }
  }
`;

export const UPDATE_DROPPOINT_ADDRESS = gql`
  mutation UPDATE_DROPPOINT_ADDRESS(
    $dropPointId: String!
    $street: String!
    $houseNumber: String!
    $postalCode: String!
    $city: String!
    $internalUserId: String
  ) {
    internalUpdateDropPointAddress(
      dropPointId: $dropPointId
      street: $street
      houseNumber: $houseNumber
      postalCode: $postalCode
      city: $city
      internalUserId: $internalUserId
    ) {
      id
      address {
        street
        houseNumber
        postalCode
        city
      }
    }
  }
`;

export const UPDATE_NA1ADL_STATUS = gql`
  mutation UPDATE_NA1ADL_STATUS($dropPointId: String!, $na1adlActive: Boolean!, $userId: String!) {
    internalUpdateNA1ADLStatus(dropPointId: $dropPointId, na1adlActive: $na1adlActive, userId: $userId) {
      id
    }
  }
`;

export const HIDE_INVENTORY_IN_APP = gql`
  mutation HIDE_INVENTORY_IN_APP($accessPointID: String!, $internalUserId: String!) {
    internalHideInventoryInApp(accessPointID: $accessPointID, internalUserId: $internalUserId)
  }
`;

export const UNHIDE_INVENTORY_IN_APP = gql`
  mutation UNHIDE_INVENTORY_IN_APP($accessPointID: String!, $internalUserId: String!) {
    internalUnhideInventoryInApp(accessPointID: $accessPointID, internalUserId: $internalUserId)
  }
`;

export const SET_PARCEL_HIDDEN_IN_APP = gql`
  mutation SET_PARCEL_HIDDEN_IN_APP($accessPointID: String!, $trackingNumber: String!, $hidden: Boolean!, $internalUserId: String!) {
    internalSetParcelHiddenInApp(
      accessPointID: $accessPointID
      trackingNumber: $trackingNumber
      hidden: $hidden
      internalUserId: $internalUserId
    )
  }
`;

export const INTERNAL_TOGGLE_AP_ACTIVE_STATUS = gql`
  mutation INTERNAL_TOGGLE_AP_ACTIVE_STATUS($accessPointID: String!, $active: Boolean!, $internalUserId: String!, $comment: String!) {
    internalToggleAccessPointActiveStatus(
      accessPointID: $accessPointID
      active: $active
      internalUserId: $internalUserId
      comment: $comment
    ) {
      id
    }
  }
`;

export const INTERNAL_SILENT_TOGGLE_DROPPOINT_STATUS = gql`
  mutation INTERNAL_SILENT_TOGGLE_DROPPOINT_STATUS($stationId: String!, $active: Boolean!, $internalUserId: String!, $comment: String!) {
    internalSilentToggleDropPointStatus(stationId: $stationId, active: $active, internalUserId: $internalUserId, comment: $comment) {
      id
    }
  }
`;

// ─── Amazon Hub Counter ──────────────────────────────────────────────────────

export type HubCounter = {
  id: string;
  hubCounterID?: string;
  amazonAccessPointId?: string;
  name?: string;
  status: string;
  isActive: boolean;
  isStaging: boolean;
  isRestrictedAccess: boolean;
  capabilities?: string[];
  capacityThreshold?: number;
  timeZone?: string;
  communicationEmail?: string;
  communicationPhone?: string;
  loginClaim?: string;
  amazonVerified: boolean;
  amazonVerifiedAt?: string;
  lastFeedId?: string;
  lastFeedStatus?: string;
  lastDeactivationTimestamp?: string;
  deactivatedUntil?: string;
  rejectionReason?: string;
  rejectionTimestamp?: string;
  createdAt: string;
  updatedAt: string;
};

export type AmazonFeedEntry = {
  id: string;
  feedId?: string;
  feedType?: string;
  operation?: string;
  status?: string;
  noProcessed?: number;
  noSuccess?: number;
  noFailed?: number;
  environment?: string;
  submittedAt?: string;
  completedAt?: string;
};

export type AmazonFeedsQuery = {
  amazonFeeds: AmazonFeedEntry[];
};

export type HubCounterStateQuery = {
  hubCounter: {
    hubCounter: HubCounter;
    displayState: string;
    createFeedCompleted: boolean;
    sessionStatus?: string;
    sessionVerified: boolean;
    sessionExpiresAt?: string;
    lastAuthorizeAt?: string;
  } | null;
};

export type AmazonHubCounterPayloadMidDayClosure = {
  startTime: string;
  endTime: string;
};

export type AmazonHubCounterPayloadStandardHours = {
  day: string;
  openingTime: string;
  closingTime: string;
  midDayClosures?: AmazonHubCounterPayloadMidDayClosure[] | null;
};

export type AmazonHubCounterPayload = {
  accessPointId: string;
  accessPointName: string;
  loginClaim?: string | null;
  isActive: boolean;
  isRestrictedAccess: boolean;
  timeZone: string;
  capacity: {
    threshold: number;
  };
  communicationDetails?: {
    emailId: string;
    phoneNumber: string;
  } | null;
  capabilities: string[];
  address: {
    addressFieldOne: string;
    city: string;
    region?: string | null;
    district?: string | null;
    postalCode: string;
    countryCode: string;
    latitude: string;
    longitude: string;
  };
  standardHours: AmazonHubCounterPayloadStandardHours[];
};

export type AmazonHubCounterPayloadQuery = {
  amazonHubCounterPayload?: AmazonHubCounterPayload | null;
};

export type AmazonHubCounterProfile = {
  hubCounterID?: string | null;
  amazonAccessPointId?: string | null;
  name?: string | null;
  loginClaim?: string | null;
  communicationEmail?: string | null;
  communicationPhone?: string | null;
  capacityThreshold?: number | null;
  capabilities?: string[] | null;
  addressFieldOne?: string | null;
  postalCode?: string | null;
  city?: string | null;
};

export type AmazonHubCounterProfileQuery = {
  amazonHubCounterProfile?: AmazonHubCounterProfile | null;
};

export type SuggestedHubCounterIdQuery = {
  suggestedHubCounterId?: string | null;
};

export type EnsurePartnerAccessPointsResult = {
  dropPointId: string;
  resolvedStatus: string;
  accessPointOutcome: string;
  hubCounterOutcome: string;
  accessPoint?: AccessPoint | null;
  hubCounter?: HubCounter | null;
};

export type ConfirmHubCounterMutation = {
  confirmHubCounter: HubCounter;
};

export type RejectHubCounterMutation = {
  rejectHubCounter: HubCounter;
};

export type EnsurePartnerAccessPointsMutation = {
  ensurePartnerAccessPoints: EnsurePartnerAccessPointsResult;
};

export const GET_HUB_COUNTER = gql`
  query GET_HUB_COUNTER($dropPointId: String!) {
    hubCounter(dropPointId: $dropPointId) {
      displayState
      createFeedCompleted
      sessionStatus
      sessionVerified
      sessionExpiresAt
      lastAuthorizeAt
      hubCounter {
        id
        hubCounterID
        amazonAccessPointId
        name
        status
        isActive
        isStaging
        isRestrictedAccess
        capabilities
        capacityThreshold
        timeZone
        communicationEmail
        communicationPhone
        loginClaim
        amazonVerified
        amazonVerifiedAt
        lastFeedId
        lastFeedStatus
        lastDeactivationTimestamp
        deactivatedUntil
        rejectionReason
        rejectionTimestamp
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_AMAZON_HUB_COUNTER_PAYLOAD = gql`
  query GET_AMAZON_HUB_COUNTER_PAYLOAD($dropPointId: String!) {
    amazonHubCounterPayload(dropPointId: $dropPointId) {
      accessPointId
      accessPointName
      loginClaim
      isActive
      isRestrictedAccess
      timeZone
      capacity {
        threshold
      }
      communicationDetails {
        emailId
        phoneNumber
      }
      capabilities
      address {
        addressFieldOne
        city
        region
        district
        postalCode
        countryCode
        latitude
        longitude
      }
      standardHours {
        day
        openingTime
        closingTime
        midDayClosures {
          startTime
          endTime
        }
      }
    }
  }
`;

export const GET_AMAZON_HUB_COUNTER_PROFILE = gql`
  query GET_AMAZON_HUB_COUNTER_PROFILE($dropPointId: String!) {
    amazonHubCounterProfile(dropPointId: $dropPointId) {
      hubCounterID
      amazonAccessPointId
      name
      loginClaim
      communicationEmail
      communicationPhone
      capacityThreshold
      capabilities
      addressFieldOne
      postalCode
      city
    }
  }
`;

export const GET_SUGGESTED_HUB_COUNTER_ID = gql`
  query GET_SUGGESTED_HUB_COUNTER_ID($dropPointId: String!) {
    suggestedHubCounterId(dropPointId: $dropPointId)
  }
`;

export const GET_AMAZON_FEEDS = gql`
  query GET_AMAZON_FEEDS($hubCounterId: String!) {
    amazonFeeds(hubCounterId: $hubCounterId) {
      id
      feedId
      feedType
      operation
      status
      noProcessed
      noSuccess
      noFailed
      environment
      submittedAt
      completedAt
    }
  }
`;

export const SUBMIT_HUB_COUNTER_TO_AMAZON = gql`
  mutation SUBMIT_HUB_COUNTER_TO_AMAZON($dropPointId: String!, $immediate: Boolean, $comment: String, $internalUserId: String) {
    submitHubCounterToAmazon(
      dropPointId: $dropPointId
      operation: CREATE
      immediate: $immediate
      comment: $comment
      internalUserId: $internalUserId
    ) {
      id
      status
      lastFeedId
      lastFeedStatus
    }
  }
`;

export const TOGGLE_HUB_COUNTER_ACTIVE = gql`
  mutation TOGGLE_HUB_COUNTER_ACTIVE(
    $dropPointId: String!
    $active: Boolean!
    $immediate: Boolean
    $comment: String
    $internalUserId: String
  ) {
    toggleHubCounterActive(
      dropPointId: $dropPointId
      active: $active
      immediate: $immediate
      comment: $comment
      internalUserId: $internalUserId
    ) {
      id
      isActive
      lastDeactivationTimestamp
      lastFeedId
      lastFeedStatus
    }
  }
`;

export const UPDATE_HUB_COUNTER = gql`
  mutation UPDATE_HUB_COUNTER($dropPointId: String!, $immediate: Boolean, $comment: String, $internalUserId: String) {
    submitHubCounterToAmazon(
      dropPointId: $dropPointId
      operation: UPDATE
      immediate: $immediate
      comment: $comment
      internalUserId: $internalUserId
    ) {
      id
      status
      lastFeedId
      lastFeedStatus
    }
  }
`;

export const CONFIRM_HUB_COUNTER = gql`
  mutation CONFIRM_HUB_COUNTER(
    $dropPointId: String!
    $hubCounterId: String
    $skipFeedCreation: Boolean
    $comment: String
    $internalUserId: String
  ) {
    confirmHubCounter(
      dropPointId: $dropPointId
      hubCounterId: $hubCounterId
      skipFeedCreation: $skipFeedCreation
      comment: $comment
      internalUserId: $internalUserId
    ) {
      id
      status
      hubCounterID
      lastFeedId
      lastFeedStatus
    }
  }
`;

export const REJECT_HUB_COUNTER = gql`
  mutation REJECT_HUB_COUNTER($dropPointId: String!, $reason: String!, $internalUserId: String) {
    rejectHubCounter(dropPointId: $dropPointId, reason: $reason, internalUserId: $internalUserId) {
      id
      status
      rejectionReason
      rejectionTimestamp
    }
  }
`;

export const UPDATE_HUB_COUNTER_STATUS = gql`
  mutation UPDATE_HUB_COUNTER_STATUS($dropPointId: String!, $status: String!, $reason: String, $internalUserId: String) {
    updateHubCounterStatus(dropPointId: $dropPointId, status: $status, reason: $reason, internalUserId: $internalUserId) {
      id
      status
      rejectionReason
      rejectionTimestamp
      updatedAt
    }
  }
`;

export type UpdateHubCounterStatusMutation = {
  updateHubCounterStatus: Pick<HubCounter, 'id' | 'status' | 'rejectionReason' | 'rejectionTimestamp' | 'updatedAt'>;
};

export const SEND_HUB_COUNTER_WAITLIST_EMAIL = gql`
  mutation SEND_HUB_COUNTER_WAITLIST_EMAIL($dropPointId: String!, $comment: String!, $internalUserId: String!) {
    sendHubCounterWaitlistEmail(dropPointId: $dropPointId, comment: $comment, internalUserId: $internalUserId)
  }
`;

export type SendHubCounterWaitlistEmailMutation = {
  sendHubCounterWaitlistEmail: boolean;
};

export const REFRESH_AMAZON_HUB_SESSION = gql`
  mutation REFRESH_AMAZON_HUB_SESSION($dropPointId: String!, $comment: String, $internalUserId: String) {
    refreshAmazonHubSession(dropPointId: $dropPointId, comment: $comment, internalUserId: $internalUserId) {
      sessionStatus
      sessionVerified
      sessionExpiresAt
      lastAuthorizeAt
      hubCounter {
        id
        amazonVerified
        amazonVerifiedAt
      }
    }
  }
`;

export const ENSURE_PARTNER_ACCESS_POINTS = gql`
  mutation ENSURE_PARTNER_ACCESS_POINTS($dropPointId: String!, $comment: String, $internalUserId: String) {
    ensurePartnerAccessPoints(dropPointId: $dropPointId, comment: $comment, internalUserId: $internalUserId) {
      dropPointId
      resolvedStatus
      accessPointOutcome
      hubCounterOutcome
      accessPoint {
        id
        accessPointID
        status
        isActive
        na1adlActive
        na1adlChangedAt
        rejectionReason
        rejectionTimestamp
        submitTimestamp
        openingHours {
          type
          monday {
            from
            to
          }
          tuesday {
            from
            to
          }
          wednesday {
            from
            to
          }
          thursday {
            from
            to
          }
          friday {
            from
            to
          }
          saturday {
            from
            to
          }
          sunday {
            from
            to
          }
        }
        idFront {
          uri
        }
        idBack {
          uri
        }
        idSelfie {
          uri
        }
        identificationSuccess
        identificationTries
        identificationSuccessTimestamp
        createdAt
        updatedAt
      }
      hubCounter {
        id
        hubCounterID
        amazonAccessPointId
        name
        status
        isActive
        isStaging
        amazonVerified
        amazonVerifiedAt
        lastFeedId
        lastFeedStatus
        lastDeactivationTimestamp
        deactivatedUntil
        rejectionReason
        rejectionTimestamp
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_AMAZON_FEED_DETAILS = gql`
  query GET_AMAZON_FEED_DETAILS($feedId: String!) {
    amazonFeed(feedId: $feedId) {
      documents
    }
  }
`;
